import api from './api';

export async function createSimulation(
  category: number | string,
  size: number,
  random?: boolean,
  moduleIds?: number[],
  moduleSlugs?: string[],
  onlyIncorrect?: boolean,
  onlyNew?: boolean,
) {
  const payload: Record<string, unknown> = { size };

  if (typeof random === 'boolean') {
    payload.random = random;
  }

  if (Array.isArray(moduleIds) && moduleIds.length > 0) {
    payload.module_ids = moduleIds;
  }

  if (Array.isArray(moduleSlugs) && moduleSlugs.length > 0) {
    payload.module_slugs = moduleSlugs;
  }

  if (typeof onlyIncorrect === 'boolean') {
    payload.only_incorrect = onlyIncorrect;
  }

  if (typeof onlyNew === 'boolean') {
    payload.only_new = onlyNew;
  }

  if (typeof category === 'number' && Number.isFinite(category)) {
    payload.category_id = category;
  } else {
    payload.category_slug = String(category);
  }

  const { data } = await api.post('simulations', payload);
  return data as { id: number };
}

export type AdaptiveSimulationPayload = {
  size?: number;
  categoryId?: number;
  categorySlug?: string;
  moduleSlugs?: string[];
  includeReview?: boolean;
  includeUnseen?: boolean;
};

export type AdaptiveSimulationResponse = {
  id: number;
  mode: 'adaptive';
  category_id: number;
  category_slug: string;
  rationale?: {
    category_source?: string;
    review_pool?: number;
    unseen_pool?: number;
  };
};

export async function createAdaptiveSimulation(
  payload: AdaptiveSimulationPayload = {},
): Promise<AdaptiveSimulationResponse> {
  const body: Record<string, unknown> = {};

  if (typeof payload.size === 'number' && Number.isFinite(payload.size) && payload.size > 0) {
    body.size = payload.size;
  }

  if (typeof payload.categoryId === 'number' && Number.isFinite(payload.categoryId)) {
    body.category_id = payload.categoryId;
  } else if (typeof payload.categorySlug === 'string' && payload.categorySlug.trim().length > 0) {
    body.category_slug = payload.categorySlug.trim();
  }

  if (Array.isArray(payload.moduleSlugs) && payload.moduleSlugs.length > 0) {
    body.module_slugs = payload.moduleSlugs;
  }

  if (typeof payload.includeReview === 'boolean') {
    body.include_review = payload.includeReview;
  }

  if (typeof payload.includeUnseen === 'boolean') {
    body.include_unseen = payload.includeUnseen;
  }

  const { data } = await api.post('simulations/adaptive', body);
  return data as AdaptiveSimulationResponse;
}

export async function getSimulation(id: string | number) {
  const { data } = await api.get(`simulations/${id}`);
  return data as {
    id: number;
    size: number;
    status: string;
    questions: { id: number; code: string; statement: string; answers: { id: number; text: string; img?: string }[]; answered?: number }[];
  };
}

export async function sendAnswer(simulationId: number, question_id: number, answer_id: number, elapsed?: number) {
  const { data } = await api.post(`simulations/${simulationId}/answers`, { question_id, answer_id, elapsed });
  return data as { saved: boolean };
}

export async function finishSimulation(simulationId: number) {
  const { data } = await api.post(`simulations/${simulationId}/finish`, {});
  return data as { correct: number; total: number; duration_seconds: number };
}

export type SimulationResultSummary = {
  correct: number;
  total: number;
  duration_seconds: number;
};

export type SimulationResultDetail = {
  question_id: number;
  chosen_answer_id: number | null;
  chosen_answer_text: string | null;
  correct_answer_id: number | null;
  correct_answer_text: string | null;
  is_correct: boolean;
};

export type SimulationResultPayload = {
  summary: SimulationResultSummary | null;
  answers: SimulationResultDetail[];
};

export async function getSimulationResults(simulationId: number): Promise<SimulationResultPayload> {
  const { data } = await api.get(`simulations/${simulationId}/results`);
  return data as SimulationResultPayload;
}

export async function listSimulations(familySlug?: string) {
  const slug = (familySlug ?? '').trim();
  const config = slug ? { params: { family: slug } } : undefined;
  const { data } = await api.get('simulations', config);
  return (Array.isArray(data) ? data : []) as Array<{
    id: number;
    status: string;
    correct: number;
    total: number;
    started_at: string;
    finished_at: string | null;
  }>;
}
